import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { AnimalModel } from '../models/animal.model';
import { AnimalMedicalConditionModel } from '../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordModel } from '../models/medical/animal-medical-record.model';
import { AnimalQuarantineModel } from '../models/medical/animal-quarantine.model';
import { DueTreatmentModel } from '../models/medical/due-treatment.model';
import {
  DueTreatmentStatus,
  getMedicalConditionKindLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  MedicalConditionSeverity
} from '../models/medical/medical.enums';
import { daysBetween, isoDatePart, todayIsoDate } from '../models/medical/medical.helpers';
import { AnimalMedicalRepository } from '../states/medical/animal-medical.repository';
import {
  activeConditions,
  isQuarantineActive,
  MedicalCounts,
  MedicalFile
} from '../states/medical/animal-medical.selectors';

export type MedicalSection = 'records' | 'prophylaxis' | 'conditions' | 'quarantine' | 'attachments';

const SECTIONS: { key: MedicalSection; label: string; icon: string; count: keyof MedicalCounts }[] = [
  { key: 'records', label: 'animalMedical.sections.records', icon: 'pi pi-book', count: 'records' },
  { key: 'prophylaxis', label: 'animalMedical.sections.prophylaxis', icon: 'pi pi-shield', count: 'prophylaxis' },
  { key: 'conditions', label: 'animalMedical.sections.conditions', icon: 'pi pi-heart', count: 'conditions' },
  { key: 'quarantine', label: 'animalMedical.sections.quarantine', icon: 'pi pi-lock', count: 'quarantines' },
  { key: 'attachments', label: 'animalMedical.sections.attachments', icon: 'pi pi-paperclip', count: 'attachments' },
];

interface DueBanner {
  overdue: number;
  due: number;
  oldest: DueTreatmentModel | null;
}

interface QuarantineBanner {
  quarantine: AnimalQuarantineModel | null;
  blocked: boolean;
  remainingDays: number | null;
}

interface ConditionsBanner {
  conditions: AnimalMedicalConditionModel[];
  visibleToAdopter: number;
  /** Eine schwere Diagnose warnt, sonst genügt der Hinweis. */
  severe: boolean;
}

@Component({
  selector: 'app-animal-details-medical',
  templateUrl: './animal-details-medical.component.html',
  standalone: false
})
export class AnimalDetailsMedicalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() animal: AnimalModel | null = null;
  /** Der Tab ist sichtbar: erst dann werden die Daten geladen. */
  @Input() active = false;

  readonly sections = SECTIONS;
  protected readonly getTypeLabelKey = getPreventiveTreatmentTypeLabelKey;
  protected readonly getCategoryLabelKey = getPreventiveTreatmentCategoryLabelKey;
  protected readonly getConditionKindLabelKey = getMedicalConditionKindLabelKey;
  section: MedicalSection = 'records';
  counts: MedicalCounts | null = null;
  loading = false;

  dueBanner: DueBanner | null = null;
  quarantineBanner: QuarantineBanner | null = null;
  conditionsBanner: ConditionsBanner | null = null;

  highlightRecordId: number | null = null;
  dialogVisible = false;
  correctionOf: AnimalMedicalRecordModel | null = null;
  previewFile: MedicalFile | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router
  ) {}

  ngOnInit(): void {
    this._route.queryParamMap.pipe(takeUntil(this._destroy$)).subscribe(params => {
      const section = params.get('medicalSection') as MedicalSection | null;
      this.section = SECTIONS.some(s => s.key === section) ? section! : 'records';
    });

    this._medicalRepository.counts$.pipe(takeUntil(this._destroy$)).subscribe(counts => this.counts = counts);
    this._medicalRepository.loading$.pipe(takeUntil(this._destroy$)).subscribe(loading => this.loading = loading);

    combineLatest([
      this._medicalRepository.dueTreatments$,
      this._medicalRepository.quarantines$,
      this._medicalRepository.activeConditions$,
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([dueTreatments, quarantines, conditions]) =>
        this.updateBanners(dueTreatments, quarantines, conditions)
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['active'] || changes['animal']) && this.active && this.animal?.id) {
      this.loadData();
    }
    if (changes['animal'] && this.animal) {
      const snapshot = this._medicalRepository.getSnapshot();
      this.updateBanners(snapshot.dueTreatments, snapshot.quarantines, activeConditions(snapshot.conditions));
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  loadData(): void {
    if (!this.animal?.id) return;
    this._medicalRepository.loadAll(this.animal.id).pipe(take(1)).subscribe();
  }

  selectSection(section: MedicalSection): void {
    this.section = section;
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { medicalSection: section },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** „zur Karteikarte #x“: Sektion wechseln und Karte hervorheben. */
  goToRecord(recordId: number): void {
    this.highlightRecordId = null;
    this.selectSection('records');
    setTimeout(() => this.highlightRecordId = recordId);
  }

  openCreate(): void {
    this.correctionOf = null;
    this.dialogVisible = true;
  }

  openCorrection(record: AnimalMedicalRecordModel): void {
    this.correctionOf = record;
    this.dialogVisible = true;
  }

  onDialogClosed(): void {
    this.dialogVisible = false;
    this.correctionOf = null;
  }

  countFor(key: keyof MedicalCounts): number {
    return this.counts?.[key] ?? 0;
  }

  private updateBanners(
    dueTreatments: DueTreatmentModel[],
    quarantines: AnimalQuarantineModel[],
    conditions: AnimalMedicalConditionModel[]
  ): void {
    this.conditionsBanner = conditions.length
      ? {
        conditions,
        visibleToAdopter: conditions.filter(condition => condition.visibleToAdopter).length,
        severe: conditions.some(condition => condition.severity === MedicalConditionSeverity.HIGH),
      }
      : null;

    const overdue = dueTreatments.filter(due => due.status === DueTreatmentStatus.OVERDUE);
    const due = dueTreatments.filter(item => item.status === DueTreatmentStatus.DUE);
    const oldest = [...overdue].sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''))[0] ?? null;
    this.dueBanner = overdue.length || due.length ? { overdue: overdue.length, due: due.length, oldest } : null;

    const today = todayIsoDate();
    const activeQuarantine = quarantines
      .filter(quarantine => isQuarantineActive(quarantine, today))
      .sort((a, b) => Number(b.blocksAdoption) - Number(a.blocksAdoption))[0] ?? null;
    const blocked = !!this.animal?.adoptionBlocked || !!activeQuarantine?.blocksAdoption;

    if (!activeQuarantine && !blocked) {
      this.quarantineBanner = null;
      return;
    }

    const endsAt = isoDatePart(activeQuarantine?.endsAt ?? this.animal?.adoptionBlockedUntil ?? null);
    this.quarantineBanner = {
      quarantine: activeQuarantine,
      blocked,
      remainingDays: endsAt ? daysBetween(today, endsAt) : null,
    };
  }
}
